import type {
    IncomeSplitConfigLegacy,
    IncomeSplitReviewContext,
} from '@fruitful/income-split';
import { sortBy } from 'lodash';
import React from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { View, YStack } from 'smoothie';

import DashCurve from '../../../../assets/images/banking/income-split/dash-curve.svg';
import DashSingle from '../../../../assets/images/banking/income-split/dash-single.svg';

// so here's how we're drawing the dashed lines:
// 1. we keep track of the heights of each rule card
// 2. we use each rule card's height minus the space required by each curved dash to determine how many straight dashes we need to render
//   2a. there are minor adjustments necessary to ensure things align properly
// 3. the curved dash handles the gaps between the rule cards
export const RegularLine = ({
    incomeSplit,
    boxHeights,
    isSorting,
    linesAnimated,
    setLinesAnimated,
    context,
}: {
    incomeSplit: IncomeSplitConfigLegacy;
    boxHeights: Record<string, number>;
    isSorting: boolean;
    linesAnimated: boolean;
    setLinesAnimated?: (arg0: boolean) => void;
    context: IncomeSplitReviewContext;
}) => {
    return sortBy(incomeSplit.rules, 'order')
        .map((rule, i) => {
            if (boxHeights[rule.id] === undefined) return null;

            const numberOfDashes = ((boxHeights[rule.id] as number) - 25) / 6;

            // don't draw line if on review screen and rendering last item
            const shouldDrawLines =
                context === 'edit' ||
                (context === 'review' && i !== incomeSplit.rules.length - 1);

            return (
                <Animated.View
                    entering={!linesAnimated ? FadeIn.duration(300) : undefined}
                    key={`lineBox-${rule.id}`}
                    onLayout={() => {
                        setLinesAnimated?.(true);
                    }}
                >
                    <YStack>
                        {!isSorting && <DashCurve height={45} width={21} />}
                        {shouldDrawLines && (
                            <YStack
                                marginTop={isSorting ? 0 : -8}
                                gap={2}
                                height={(boxHeights[rule.id] as number) - 21}
                                flexDirection={
                                    isSorting ? 'column' : 'column-reverse'
                                }
                            >
                                {Array(
                                    Math.floor(
                                        numberOfDashes < 0 ? 0 : numberOfDashes,
                                    ),
                                )
                                    .fill(0)
                                    .map((_, i) => {
                                        return (
                                            <View
                                                key={`dash-${rule.id}-${i}`}
                                                marginLeft={0.4}
                                            >
                                                <DashSingle height={4} />
                                            </View>
                                        );
                                    })}
                                <DashSingle height={4} />
                            </YStack>
                        )}
                    </YStack>
                </Animated.View>
            );
        })
        .filter((i) => i);
};

// this is the straight line we show when rules are being sorted (we hide the curved dashes)
// the line height is determined by adding up all the heights of the rule cards + the add rule button + the gaps between
export const SortingLine = ({
    boxHeights,
}: {
    boxHeights: Record<string, number>;
}) => {
    const straightLineHeight =
        56 +
        Object.values(boxHeights).reduce((acc, height) => acc + height, 0) +
        Object.values(boxHeights).length * 16;

    const straightLineDashCount = Math.ceil(straightLineHeight / 6);

    return (
        <YStack gap={2} height={straightLineHeight}>
            {Array(straightLineDashCount)
                .fill(0)
                .map((_, i) => {
                    return (
                        <View key={`dash-${i}`} marginLeft={0.4}>
                            <DashSingle height={4} />
                        </View>
                    );
                })}
        </YStack>
    );
};
